import React, { useState } from 'react';
import { FileText, Search, Download, Filter } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import DualScrollTable from '../../components/common/DualScrollTable';
import { getAuditLogs, searchAuditLogs } from '../../services/supabase/audit-logs';
import { downloadCsv } from '../../utils/exportUtils';

export default function AuditLogs() {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: logs, isLoading } = useQuery({
    queryKey: ['audit-logs', searchQuery],
    queryFn: () => searchQuery ? searchAuditLogs(searchQuery) : getAuditLogs()
  });
  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <FileText className="h-6 w-6 text-gray-400 mr-2" />
        <h2 className="text-lg font-medium text-gray-900">Audit Logs</h2>
      </div>

      <div className="mb-6 flex justify-between">
        <div className="flex-1 max-w-lg relative">
          <input
            type="text"
            placeholder="Search audit logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        
        <div className="flex gap-4">
          <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
            <Filter className="h-5 w-5 mr-2" />
            Filter
          </button>
          <button 
            onClick={() => {
              if (!logs?.length) return;
              
              const headers = ['Timestamp', 'Action', 'User', 'Details'].join(',');
              const rows = logs.map(log => [
                new Date(log.timestamp).toLocaleString(),
                log.action,
                log.user_name,
                log.details
              ].map(value => `"${value}"`).join(','));

              downloadCsv([headers, ...rows].join('\n'), `audit-logs-${new Date().toISOString()}.csv`);
            }}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={!logs?.length}
          >
            <Download className="h-5 w-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                </td>
              </tr>
            ) : !logs?.length ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  No audit logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {log.action}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {log.user_name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {log.details}
                </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </DualScrollTable>
      </div>
    </div>
  );
}
